import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import secureAxios from "../../utils/secureAxios";

const UserLoginTracker = () => {
  const { user } = useContext(AuthContext);

  // SIMPLIFIED STATE: Single users array with all login data
  const [users, setUsers] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metadata, setMetadata] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({
    selectedTenant: "",
    searchUser: "",
    roleFilter: "",
    statusFilter: "",
  });

  // Summary statistics (now come from backend metadata)
  const [summary, setSummary] = useState({
    currentlyActive: 0,
    totalUsers: 0,
    activeToday: 0,
    neverLoggedIn: 0,
  });

  // Load tenants for dropdown (for primary users only)
  useEffect(() => {
    if (user?.is_primary) {
      const fetchTenants = async () => {
        try {
          const res = await secureAxios.get("/api/tenants");
          setTenants(res.data || []);
        } catch (err) {
          console.error("Error loading tenants:", err);
          setError("Failed to load tenants for filter");
        }
      };
      fetchTenants();
    }
  }, [user?.is_primary]);

  // MAIN DATA LOADING: Use enhanced login-tracking endpoint
  useEffect(() => {
    if (!user?.tenant_id) return;

    const loadUserLoginData = async () => {
      setLoading(true);
      setError(null);

      try {
        console.log(
          "Loading user login data from /api/users/login-tracking"
        );

        // USE YOUR ENHANCED ENDPOINT that pulls from active_session_tokens
        const response = await secureAxios.get("/api/users/login-tracking");

        console.log("Received login tracking response:", response.data);

        if (response.data.success) {
          const userData = response.data.users || [];
          const metadataInfo = response.data.metadata || {};

          setUsers(userData);
          setMetadata(metadataInfo);

          // Update summary from backend metadata
          setSummary({
            currentlyActive: metadataInfo.currently_active || 0,
            totalUsers: metadataInfo.total_users || 0,
            activeToday: metadataInfo.users_with_recent_activity || 0,
            neverLoggedIn: metadataInfo.never_logged_in || 0,
          });

          console.log(
            "Successfully loaded",
            userData.length,
            "users with login data"
          );

          // DEBUG: Log specific user data
          const lynnette = userData.find(
            (u) => u.email === "missy.ann.wyatt@outlook.com"
          );
          if (lynnette) {
            console.log("Lynnette's frontend data:", {
              email: lynnette.email,
              last_login: lynnette.last_login,
              session_duration: lynnette.session_duration,
              login_history: lynnette.login_history,
              total_sessions: lynnette.total_sessions,
              is_currently_active: lynnette.is_currently_active,
            });
          }
        } else {
          throw new Error(
            response.data.error || "Failed to load login tracking data"
          );
        }
      } catch (err) {
        console.error("Error loading user login data:", err);
        const errorMsg =
          err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "Unknown error occurred";
        setError(`Failed to load user login data: ${errorMsg}`);
      } finally {
        setLoading(false);
      }
    };

    loadUserLoginData();
  }, [user?.tenant_id, filters.selectedTenant]);

  // Apply filters to users data
  useEffect(() => {
    let filtered = [...users];

    // Search filter
    if (filters.searchUser.trim()) {
      const searchTerm = filters.searchUser.toLowerCase().trim();
      filtered = filtered.filter(
        (user) =>
          (user.name || "").toLowerCase().includes(searchTerm) ||
          (user.email || "").toLowerCase().includes(searchTerm)
      );
    }

    // Role filter
    if (filters.roleFilter) {
      filtered = filtered.filter(
        (user) =>
          (user.role || "").toLowerCase() === filters.roleFilter.toLowerCase()
      );
    }

    // Status filter
    if (filters.statusFilter) {
      filtered = filtered.filter((user) => {
        if (filters.statusFilter === "active") {
          return user.is_currently_active;
        } else if (filters.statusFilter === "inactive") {
          return user.has_ever_logged_in && !user.is_currently_active;
        } else if (filters.statusFilter === "never_logged_in") {
          return !user.has_ever_logged_in;
        }
        return true;
      });
    }

    setFilteredData(filtered);
  }, [users, filters]);

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
  };

  // Utility functions for display
  const getStatusBadgeClass = (user) => {
    if (user.is_currently_active) return "bg-success";
    if (user.has_ever_logged_in) return "bg-secondary";
    return "bg-warning";
  };

  const getStatusText = (user) => {
    if (user.is_currently_active) return "Active Now";
    if (user.has_ever_logged_in) return "Inactive";
    return "Never Logged In";
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "Never";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid Date";

      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      // Show relative time for recent activity
      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;

      // Show formatted date for older activity
      return (
        date.toLocaleDateString() +
        " " +
        date.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
      );
    } catch (error) {
      console.error("Date formatting error:", error);
      return "Invalid Date";
    }
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case "admin":
        return "bg-danger";
      case "counselor":
        return "bg-primary";
      case "job coach":
        return "bg-success";
      default:
        return "bg-secondary";
    }
  };

  if (loading) {
    return (
      <div className="container-fluid py-3 py-md-4">
        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">
                  Loading user login data...
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid py-3 py-md-4">
        <div className="row">
          <div className="col-12">
            <div className="alert alert-danger">
              <i className="bi bi-exclamation-triangle me-2"></i>
              <strong>Error:</strong> {error}
              <button
                className="btn btn-outline-danger btn-sm ms-3"
                onClick={() => window.location.reload()}
              >
                <i className="bi bi-arrow-clockwise me-1"></i>
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-3 py-md-4">
      <div className="row">
        <div className="col-12">
          {/* Header */}
          <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-3 mb-md-4 gap-2">
            <div>
              <h2 className="mb-1 h4 h2-md">
                <i className="bi bi-people me-2"></i>
                User Login Tracking
              </h2>
              <p className="text-muted mb-0 small d-none d-sm-block">
                Enhanced login tracking with historical data from
                active_session_tokens
              </p>
            </div>

            <button
              className="btn btn-outline-primary"
              onClick={() => window.location.reload()}
              disabled={loading}
            >
              <i className="bi bi-arrow-clockwise me-1"></i>
              Refresh
            </button>
          </div>

          {/* Summary Statistics - 2x2 grid on mobile, 4 columns on desktop */}
          <div className="row mb-3 mb-md-4 g-2 g-md-3">
            <div className="col-6 col-md-3">
              <div className="card bg-success text-white h-100">
                <div className="card-body p-2 p-md-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-1 small">
                        <span className="d-none d-sm-inline">Currently </span>Active
                      </h6>
                      <h4 className="mb-0">{summary.currentlyActive}</h4>
                    </div>
                    <i className="bi bi-person-check fs-3 fs-md-2 d-none d-sm-block"></i>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="card bg-primary text-white h-100">
                <div className="card-body p-2 p-md-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-1 small">Total Users</h6>
                      <h4 className="mb-0">{summary.totalUsers}</h4>
                    </div>
                    <i className="bi bi-people fs-3 fs-md-2 d-none d-sm-block"></i>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="card bg-info text-white h-100">
                <div className="card-body p-2 p-md-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-1 small">
                        <span className="d-none d-sm-inline">Recent </span>Activity
                      </h6>
                      <h4 className="mb-0">{summary.activeToday}</h4>
                    </div>
                    <i className="bi bi-clock-history fs-3 fs-md-2 d-none d-sm-block"></i>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="card bg-warning text-dark h-100">
                <div className="card-body p-2 p-md-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-1 small">Never Logged In</h6>
                      <h4 className="mb-0">{summary.neverLoggedIn}</h4>
                    </div>
                    <i className="bi bi-person-x fs-3 fs-md-2 d-none d-sm-block"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="card mb-3 mb-md-4">
            <div className="card-header">
              <h5 className="mb-0 h6">
                <i className="bi bi-funnel me-2"></i>
                Filters
              </h5>
            </div>
            <div className="card-body p-2 p-md-3">
              <div className="row g-2 g-md-3">
                <div className="col-12 col-sm-6 col-md-3">
                  <label className="form-label small">Search User</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Name or email..."
                    value={filters.searchUser}
                    onChange={(e) =>
                      handleFilterChange("searchUser", e.target.value)
                    }
                  />
                </div>
                <div className="col-6 col-md-3">
                  <label className="form-label small">Role</label>
                  <select
                    className="form-select"
                    value={filters.roleFilter}
                    onChange={(e) =>
                      handleFilterChange("roleFilter", e.target.value)
                    }
                  >
                    <option value="">All Roles</option>
                    <option value="admin">Admin</option>
                    <option value="counselor">Counselor</option>
                    <option value="job coach">Job Coach</option>
                    <option value="rsa">RSA</option>
                  </select>
                </div>
                <div className="col-6 col-md-3">
                  <label className="form-label small">Status</label>
                  <select
                    className="form-select"
                    value={filters.statusFilter}
                    onChange={(e) =>
                      handleFilterChange("statusFilter", e.target.value)
                    }
                  >
                    <option value="">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="never_logged_in">Never Logged In</option>
                  </select>
                </div>
                <div className="col-12 col-md-3 d-flex align-items-end">
                  <div className="w-100">
                    <span className="badge bg-secondary">
                      {filteredData.length} users shown
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Users Table/Cards */}
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0 h6">
                <i className="bi bi-people me-2"></i>
                <span className="d-none d-sm-inline">User Login </span>Activity
              </h5>
              <span className="badge bg-primary">
                {filteredData.length} users
              </span>
            </div>
            <div className="card-body p-0">
              {filteredData.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-people fs-1 text-muted mb-3 d-block"></i>
                  <h5>No users found</h5>
                  <p className="text-muted">
                    No users match your current filters.
                  </p>
                </div>
              ) : (
                <>
                  {/* Desktop Table View */}
                  <div className="table-responsive d-none d-lg-block">
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>
                            <i className="bi bi-person me-1"></i>
                            User
                          </th>
                          <th>
                            <i className="bi bi-shield-check me-1"></i>
                            Role
                          </th>
                          <th>
                            <i className="bi bi-activity me-1"></i>
                            Status
                          </th>
                          <th>
                            <i className="bi bi-clock me-1"></i>
                            Last Login
                          </th>
                          <th>
                            <i className="bi bi-clock-history me-1"></i>
                            Session
                          </th>
                          <th>
                            <i className="bi bi-journal-text me-1"></i>
                            History
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredData.map((userData) => (
                          <tr key={userData.user_id}>
                            <td>
                              <div>
                                <div className="fw-medium">{userData.name}</div>
                                <small className="text-muted">
                                  {userData.email}
                                </small>
                              </div>
                            </td>
                            <td>
                              <span
                                className={`badge ${getRoleBadgeClass(
                                  userData.role
                                )}`}
                              >
                                {userData.role || "Unknown"}
                              </span>
                            </td>
                            <td>
                              <span
                                className={`badge ${getStatusBadgeClass(
                                  userData
                                )}`}
                              >
                                {getStatusText(userData)}
                              </span>
                              {userData.is_currently_active && (
                                <small className="text-success d-block">
                                  <i className="bi bi-dot"></i>
                                  Online now
                                </small>
                              )}
                            </td>
                            <td>
                              <div>{formatDateTime(userData.last_login)}</div>
                              {userData.last_login && (
                                <small className="text-muted">
                                  {new Date(
                                    userData.last_login
                                  ).toLocaleDateString()}
                                </small>
                              )}
                            </td>
                            <td>
                              {userData.session_duration !== "N/A" ? (
                                <span className="badge bg-light text-dark">
                                  {userData.session_duration}
                                </span>
                              ) : (
                                <span className="text-muted">N/A</span>
                              )}
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                {userData.has_ever_logged_in ? (
                                  <span className="text-success">
                                    <i className="bi bi-check-circle me-1"></i>
                                    {userData.login_history}
                                  </span>
                                ) : (
                                  <span className="text-warning">
                                    <i className="bi bi-exclamation-circle me-1"></i>
                                    Never logged in
                                  </span>
                                )}
                              </div>
                              {userData.total_sessions > 0 && (
                                <small className="text-muted d-block">
                                  {userData.total_sessions} total session
                                  {userData.total_sessions !== 1 ? "s" : ""}
                                  {userData.active_sessions_count > 0 && (
                                    <span className="text-success">
                                      {" - "}
                                      {userData.active_sessions_count} active
                                    </span>
                                  )}
                                </small>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile/Tablet Card View */}
                  <div className="d-block d-lg-none">
                    {filteredData.map((userData) => (
                      <div
                        key={userData.user_id}
                        className="border-bottom p-3"
                      >
                        {/* User info and status */}
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="flex-grow-1 me-2">
                            <div className="fw-medium">{userData.name}</div>
                            <small className="text-muted text-break">
                              {userData.email}
                            </small>
                          </div>
                          <span
                            className={`badge ${getStatusBadgeClass(userData)}`}
                          >
                            {userData.is_currently_active ? (
                              <>
                                <i className="bi bi-circle-fill me-1" style={{ fontSize: "0.5rem" }}></i>
                                Online
                              </>
                            ) : (
                              getStatusText(userData)
                            )}
                          </span>
                        </div>

                        {/* Details row */}
                        <div className="d-flex flex-wrap gap-2 align-items-center small">
                          <span
                            className={`badge ${getRoleBadgeClass(
                              userData.role
                            )}`}
                          >
                            {userData.role || "Unknown"}
                          </span>

                          {userData.last_login && (
                            <span className="text-muted">
                              <i className="bi bi-clock me-1"></i>
                              {formatDateTime(userData.last_login)}
                            </span>
                          )}

                          {userData.session_duration &&
                            userData.session_duration !== "N/A" && (
                              <span className="badge bg-light text-dark">
                                {userData.session_duration}
                              </span>
                            )}
                        </div>

                        {/* Session info */}
                        {userData.total_sessions > 0 && (
                          <div className="mt-2 small text-muted">
                            <i className="bi bi-journal-text me-1"></i>
                            {userData.total_sessions} session
                            {userData.total_sessions !== 1 ? "s" : ""}
                            {userData.active_sessions_count > 0 && (
                              <span className="text-success ms-1">
                                ({userData.active_sessions_count} active)
                              </span>
                            )}
                          </div>
                        )}

                        {!userData.has_ever_logged_in && (
                          <div className="mt-2 small text-warning">
                            <i className="bi bi-exclamation-circle me-1"></i>
                            Never logged in
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Metadata Footer */}
          {metadata && (
            <div className="card mt-3 mt-md-4">
              <div className="card-body p-2 p-md-3">
                <h6 className="small">Query Information</h6>
                <div className="row">
                  <div className="col-12 col-md-6">
                    <small className="text-muted">
                      <strong>Query Time:</strong>{" "}
                      {new Date(metadata.query_timestamp).toLocaleString()}
                      <br />
                      <strong>Admin User:</strong> {metadata.admin_user}
                      <span className="d-none d-sm-inline">
                        <br />
                        <strong>Includes Historical Data:</strong>{" "}
                        {metadata.includes_historical_data ? "Yes" : "No"}
                      </span>
                    </small>
                  </div>
                  <div className="col-12 col-md-6 mt-2 mt-md-0">
                    <small className="text-muted">
                      <strong>Total Users:</strong> {metadata.total_users}
                      <br />
                      <strong>Currently Active:</strong>{" "}
                      {metadata.currently_active}
                      <br />
                      <strong>Never Logged In:</strong>{" "}
                      {metadata.never_logged_in}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* HIPAA Compliance Footer */}
          <div className="text-center text-muted small mt-3 mt-md-4">
            <hr />
            <p className="mb-2">
              <i className="bi bi-shield-check me-1"></i>
              This system is HIPAA-compliant and all activities are monitored.
            </p>
            <p className="d-none d-sm-block">
              User login tracking is for security monitoring and access control
              purposes only. Data source: active_session_tokens with historical
              session analysis.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserLoginTracker;